// The signed-in user's settings, for the whole app: one Program
// (settings/program.ts) that every view reads. The Files view asks it
// whether GitHub's "Viewed" follows the review marks; the Settings view
// shows and changes it.
//
// A new account signing in resets it (the settings are the account's);
// `open` reads them once someone is signed in.
import { onIdentityChange } from '../api/identity';
import { getPreferences, putPreference } from '../api/server';
import { Program } from '../runtime/effects';
import type { Remote } from '../runtime/remote';
import { toState } from '../runtime/state.svelte';
import { prefs } from './prefs.svelte';
import {
  saving,
  serverSettings,
  settingsProgram,
  viewedSyncChoice,
  viewedSyncOn,
  VIEWED_PREFERENCE,
  type ServerSettings,
  type SettingsMsg,
  type SettingsOut,
  type SettingsState,
  type SettingsWorld,
  type ViewedHealth,
} from './settings/program';

/** The settings as the server keeps them, for this page's account. */
export const serverWorld: Pick<SettingsWorld, 'read' | 'write'> = {
  read: async () => {
    const p = await getPreferences();
    return serverSettings(p.preferences, p.revisions);
  },
  write: (on, from) => async () => {
    const p = await putPreference(VIEWED_PREFERENCE, on, from);
    return p === 'conflict' ? p : serverSettings(p.preferences, p.revisions);
  },
};

export class Settings {
  private program: Program<SettingsState, SettingsMsg, SettingsOut>;
  private view: { readonly current: SettingsState };

  constructor(world: SettingsWorld, o: { dropLegacy: () => void; resetOn?: (listener: () => void) => () => void }) {
    this.program = new Program<SettingsState, SettingsMsg, SettingsOut>({
      ...settingsProgram(world),
      port: (out) => {
        if (out.kind === 'drop-legacy') o.dropLegacy();
      },
      resetOn: o.resetOn,
    });
    this.view = toState(this.program);
  }

  /** What the server holds, or how reading it stands. */
  get server(): Remote<ServerSettings> {
    return this.view.current.server;
  }

  /** Whether review marks and GitHub's "Viewed" follow each other: false until the user's choice is known. */
  get viewedSync(): boolean {
    return viewedSyncOn(this.view.current);
  }

  /** The "Viewed" choice to show; null while it is not known. */
  get viewedSyncChoice(): boolean | null {
    return viewedSyncChoice(this.view.current);
  }

  /** The user's change is on its way to the server. */
  get saving(): boolean {
    return saving(this.view.current);
  }

  /** The last change did not last: what was asked for, and why not. */
  get refused(): SettingsState['refused'] {
    return this.view.current.refused;
  }

  /** The sync's trouble on the pull request last looked at; null when it had none. */
  get viewedHealth(): ViewedHealth | null {
    return this.view.current.health;
  }

  /** Someone is signed in: read their settings (once). */
  open() {
    this.program.send({ t: 'open' });
  }

  /** The settings may have changed elsewhere (another page said so, or this one is looked at again). */
  refresh() {
    this.program.send({ t: 'refresh' });
  }

  setViewedSync(on: boolean) {
    this.program.send({ t: 'set-viewed-sync', on });
  }

  dismissRefusal() {
    this.program.send({ t: 'dismiss' });
  }

  /** How the "Viewed" sync fares on the pull request shown. */
  reportViewed(health: ViewedHealth) {
    this.program.send({ t: 'viewed-health', health });
  }
}

export const settings = new Settings(
  { ...serverWorld, legacyOff: () => prefs.legacyViewedOff, now: Date.now },
  { dropLegacy: () => prefs.dropLegacyViewed(), resetOn: onIdentityChange },
);
